import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Grid, 
  Card, 
  CardContent,
  Avatar,
  Rating,
  IconButton,
  useMediaQuery,
  useTheme
} from '@mui/material';
import styled from '@emotion/styled';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

// Styled components
const TestimonialsSection = styled(Box)(({ theme }) => ({
  backgroundImage: 'url(/assets/images/bg/testi-bg.png)',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
  padding: '100px 0',
  position: 'relative',
  '&:before': {
    content: '""',
    position: 'absolute',
    left: 0,
    top: 0,
    width: '100%',
    height: '100%',
  },
  [theme.breakpoints.down('md')]: {
    padding: '70px 0',
  }
}));

const TestimonialContainer = styled(Container)(({ theme }) => ({
  position: 'relative',
  zIndex: 1,
}));

const TestimonialImage = styled(Box)(({ theme }) => ({
  [theme.breakpoints.down('md')]: {
    marginBottom: '40px',
    textAlign: 'center',
  },
  '& img': {
    maxWidth: '100%',
    borderRadius: '5px',
  }
}));

const TestimonialSlider = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  overflow: 'visible',
  padding: '0 60px',
  [theme.breakpoints.up('md')]: {
    paddingLeft: '60px',
    paddingRight: '60px',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '0 20px',
  }
}));

const TestimonialCard = styled(Card)(({ theme }) => ({
  backgroundColor: '#ffffff',
  borderRadius: '5px',
  padding: '30px',
  boxShadow: '0 5px 20px rgba(0, 0, 0, 0.1)',
  marginBottom: '30px',
  height: '100%',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'all 0.5s ease',
  '&:hover': {
    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)',
  }
}));

const QuoteIcon = styled(FormatQuoteIcon)(({ theme }) => ({
  fontSize: '40px',
  color: '#ff0000',
  marginBottom: '15px',
}));

const TestimonialContent = styled(Typography)(({ theme }) => ({
  fontSize: '16px',
  lineHeight: '28px',
  marginBottom: '20px',
  fontStyle: 'italic',
}));

const TestimonialAuthor = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
}));

const AuthorAvatar = styled(Avatar)(({ theme }) => ({
  width: '60px',
  height: '60px',
  marginRight: '15px',
}));

const AuthorInfo = styled(Box)(({ theme }) => ({
  flex: 1,
}));

const AuthorName = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  marginBottom: '5px',
}));

const AuthorPosition = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: '14px',
}));

const NavigationDots = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  marginTop: '30px',
  [theme.breakpoints.down('sm')]: {
    marginTop: '20px',
  }
}));

const Dot = styled(Box)(({ theme, active }) => ({
  width: '12px',
  height: '12px',
  borderRadius: '50%',
  backgroundColor: active ? '#ff0000' : '#cccccc',
  margin: '0 5px',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  [theme.breakpoints.down('sm')]: {
    width: '10px',
    height: '10px',
  }
}));

const CarouselContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  transition: 'transform 0.7s ease',
  marginBottom: '20px',
  touchAction: 'pan-y',
}));

const NavigationButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  backgroundColor: 'rgba(255, 255, 255, 0.8)',
  color: '#ff0000',
  zIndex: 10,
  width: '40px',
  height: '40px',
  boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
  '&:hover': {
    backgroundColor: '#ff0000',
    color: '#ffffff',
  },
  '&.prev': {
    left: '-10px',
  },
  '&.next': {
    right: '-10px',
  },
  '&.Mui-disabled': {
    backgroundColor: 'rgba(255, 255, 255, 0.5)',
    color: 'rgba(0, 0, 0, 0.26)',
  },
  [theme.breakpoints.down('sm')]: {
    display: 'none !important',
  }
}));

function Testimonials() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [activeSlide, setActiveSlide] = useState(0);
  const [slideWidth, setSlideWidth] = useState(0);
  const [maxSlide, setMaxSlide] = useState(0);
  
  const testimonials = [
    {
      id: 1,
      content: "By entrusting our entire cybersecurity management to HKMSP, they've reimagined our security measures, enabling us to offer innovative and secure banking services with absolute peace of mind",
      author: "Matteo Beorchia",
      position: "CEO at First Digital Trade",
      avatar: "/assets/images/testimonial/avatar/3.jpg",
      rating: 5
    },
    {
      id: 2,
      content: "As a small business owner, cybersecurity can be daunting, but this company has made it accessible and straightforward. The customer service team went above and beyond to ensure that my staff and I understood the systems in place. They were patient and informative, providing training that was invaluable. Since partnering with them, we've noticed a significant improvement in our network security. Prices are competitive, offering great value for such high-quality service",
      author: "Peter Alexander",
      position: "Owner @ Gumtree Pty Ltd",
      avatar: "/assets/images/testimonial/avatar/2.jpg",
      rating: 5
    },
    {
      id: 3,
      content: "HKMSP support is excellent and the team is very professional, responsive, and knowledgeable. They are very supportive and willing to help at any time. I would recommend HKMSP to anyone who is looking for a professional cybersecurity service provider.",
      author: "Nick Forshteyn",
      position: "Cintelis PTY LTD",
      avatar: "/assets/images/testimonial/avatar/3.webp",
      rating: 5
    }
  ];
  
  // Track touch/drag events
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  
  useEffect(() => {
    // Calculate the width of slides for proper sliding
    const updateDimensions = () => {
      const containerWidth = document.querySelector('.testimonial-container')?.offsetWidth || window.innerWidth * 0.8;
      setSlideWidth(containerWidth);
      setMaxSlide(testimonials.length - 1);
    };
    
    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    
    return () => {
      window.removeEventListener('resize', updateDimensions);
    };
  }, [testimonials.length]);
  
  // Auto-slide for mobile devices
  useEffect(() => {
    let autoSlideInterval;
    
    if (isMobile) {
      autoSlideInterval = setInterval(() => {
        setActiveSlide(prev => {
          if (prev >= maxSlide) {
            return 0;
          }
          return prev + 1;
        });
      }, 5000); // Change slide every 5 seconds
    }
    
    return () => {
      if (autoSlideInterval) {
        clearInterval(autoSlideInterval);
      }
    };
  }, [isMobile, maxSlide]);
  
  // Handle touch events for mobile swiping
  const handleTouchStart = (e) => {
    setTouchStart(e.targetTouches[0].clientX);
    setIsDragging(true);
  };
  
  const handleTouchMove = (e) => {
    if (isDragging) {
      setTouchEnd(e.targetTouches[0].clientX);
    }
  };
  
  const handleTouchEnd = () => {
    setIsDragging(false);
    if (touchStart - touchEnd > 75) { // Minimum swipe distance
      handleNext();
    } else if (touchStart - touchEnd < -75) {
      handlePrev();
    }
  };
  
  const handlePrev = () => {
    setActiveSlide(prev => Math.max(0, prev - 1));
  };
  
  const handleNext = () => {
    setActiveSlide(prev => Math.min(testimonials.length - 1, prev + 1));
  };

  return (
    <TestimonialsSection>
      <TestimonialContainer maxWidth="lg">
        <Grid container spacing={4} alignItems="center">
          <Grid item xs={12} md={5}>
            <TestimonialImage>
              <img src="/assets/images/testimonial/testi.png" alt="Testimonial" />
            </TestimonialImage>
          </Grid>
          <Grid item xs={12} md={7}>
            <TestimonialSlider>
              <NavigationButton 
                className="prev" 
                onClick={handlePrev} 
                disabled={activeSlide === 0}
                aria-label="Previous testimonial"
              >
                <ArrowBackIcon />
              </NavigationButton>
              
              <NavigationButton 
                className="next" 
                onClick={handleNext} 
                disabled={activeSlide >= maxSlide}
                aria-label="Next testimonial"
              >
                <ArrowForwardIcon />
              </NavigationButton>
              
              <Box 
                className="testimonial-container"
                sx={{ 
                  overflow: 'hidden', 
                  width: '100%', 
                  position: 'relative', 
                  padding: '10px 0'
                }}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
              >
                <CarouselContainer 
                  sx={{ 
                    transform: `translateX(-${activeSlide * slideWidth}px)`,
                    width: `${testimonials.length * slideWidth}px`,
                  }}
                >
                  {testimonials.map((testimonial) => (
                    <Box 
                      key={testimonial.id}
                      sx={{ 
                        width: `${slideWidth}px`,
                        padding: '0 10px',
                        boxSizing: 'border-box'
                      }}
                    >
                      <TestimonialCard elevation={0}>
                        <CardContent>
                          <QuoteIcon />
                          <TestimonialContent variant="body1">
                            {testimonial.content}
                          </TestimonialContent>
                          <Rating value={testimonial.rating} readOnly sx={{ mb: 2, mt: 2 }} />
                          <TestimonialAuthor>
                            <AuthorAvatar src={testimonial.avatar} alt={testimonial.author} />
                            <AuthorInfo>
                              <AuthorName variant="h6">{testimonial.author}</AuthorName>
                              <AuthorPosition variant="body2">{testimonial.position}</AuthorPosition>
                            </AuthorInfo>
                          </TestimonialAuthor>
                        </CardContent>
                      </TestimonialCard>
                    </Box>
                  ))}
                </CarouselContainer>
              </Box>
              
              <NavigationDots>
                {testimonials.map((_, index) => (
                  <Dot 
                    key={index} 
                    active={index === activeSlide ? 1 : 0} 
                    onClick={() => setActiveSlide(index)}
                  />
                ))}
              </NavigationDots>
            </TestimonialSlider>
          </Grid>
        </Grid>
      </TestimonialContainer>
    </TestimonialsSection>
  );
}

export default Testimonials;
